import { ref, onUnmounted } from "vue";
import { performTokenRefresh } from "@/utils/tokenUtils";
import Cookies from "js-cookie";

const REFRESH_THRESHOLD_MINUTES = 5;
const MIN_REFRESH_INTERVAL_MINUTES = 2;

let refreshCheckInterval = null;
let lastRefreshTime = 0;
let isRefreshing = false;
let visibilityListener = null;
let pageHiddenTime = null;
let lastKnownExpireTime = 0;

export function useTokenRefresh() {
  const isTokenRefreshActive = ref(false);

  const getTokenDebugInfo = () => {
    const expireTimeMs = parseInt(Cookies.get("expireTime"), 10);
    const now = Date.now();
    const timeRemaining = expireTimeMs - now;
    const minutesRemaining = Math.floor(timeRemaining / (60 * 1000));

    return {
      expireTimeMs,
      now,
      timeRemaining,
      minutesRemaining,
      refreshToken: Cookies.get("refreshToken") ? "exists" : "missing",
      accessToken: Cookies.get("accessToken") ? "exists" : "missing",
      lastRefreshTime,
      isRefreshing,
    };
  };

  const checkAndRefreshToken = async () => {
    try {
      const expireTimeMs = parseInt(Cookies.get("expireTime"), 10);
      const refreshToken = Cookies.get("refreshToken");

      if (!expireTimeMs || !refreshToken) {
        return;
      }

      const now = Date.now();
      const timeUntilExpiry = expireTimeMs - now;
      const thresholdMs = REFRESH_THRESHOLD_MINUTES * 60 * 1000;

      // Detect if cookie was manually changed
      if (lastKnownExpireTime !== 0 && expireTimeMs !== lastKnownExpireTime) {
      }
      lastKnownExpireTime = expireTimeMs;

      if (timeUntilExpiry <= thresholdMs) {
        const timeSinceLastRefresh = now - lastRefreshTime;
        const minRefreshIntervalMs = MIN_REFRESH_INTERVAL_MINUTES * 60 * 1000;

        if (timeSinceLastRefresh < minRefreshIntervalMs || isRefreshing) {
          return;
        }

        isRefreshing = true;
        const secondsRemaining = Math.floor(timeUntilExpiry / 1000);

        const result = await performTokenRefresh(REFRESH_THRESHOLD_MINUTES);

        if (result.success) {
          lastRefreshTime = now;
        } else {
          console.warn("[Token Refresh] Proactive refresh failed:", result.error);
        }

        isRefreshing = false;
      }
    } catch (error) {
      console.error("[Token Refresh] Check and refresh error:", error);
      isRefreshing = false;
    }
  };

  const handleVisibilityChange = async () => {
    if (document.hidden) {
      pageHiddenTime = Date.now();
    } else {
      
      const expireTimeMs = parseInt(Cookies.get("expireTime"), 10);
      const refreshToken = Cookies.get("refreshToken");
      const now = Date.now();

      if (!expireTimeMs || !refreshToken) {
        return;
      }

      const timeUntilExpiry = expireTimeMs - now;

      if (timeUntilExpiry <= 0) {
        isRefreshing = true;
        const result = await performTokenRefresh(0);
        if (result.success) {
          lastRefreshTime = now;
        } else {
        }
        isRefreshing = false;
      } else if (timeUntilExpiry <= REFRESH_THRESHOLD_MINUTES * 60 * 1000) {
        await checkAndRefreshToken();
      }
    }
  };

  const handlePageReload = async () => {
    const expireTimeMs = parseInt(Cookies.get("expireTime"), 10);
    const refreshToken = Cookies.get("refreshToken");
    const now = Date.now();

    if (!expireTimeMs || !refreshToken) {
      return;
    }

    const timeUntilExpiry = expireTimeMs - now;

    if (timeUntilExpiry <= 0) {
      isRefreshing = true;
      const result = await performTokenRefresh(0);
      if (result.success) {
        lastRefreshTime = now;
      } else {
      }
      isRefreshing = false;
    } else if (timeUntilExpiry <= REFRESH_THRESHOLD_MINUTES * 60 * 1000) {
      await checkAndRefreshToken();
    }
  };

  const startTokenRefresh = async () => {
    if (isTokenRefreshActive.value) {
      return;
    }

    isTokenRefreshActive.value = true;
    lastRefreshTime = Date.now();

    await handlePageReload();

    // Check every 30 seconds instead of 1 minute for faster detection
    refreshCheckInterval = setInterval(() => {
      checkAndRefreshToken();
    }, 30 * 1000);

    visibilityListener = handleVisibilityChange;
    document.addEventListener("visibilitychange", visibilityListener);
  };

  const stopTokenRefresh = () => {
    if (refreshCheckInterval) {
      clearInterval(refreshCheckInterval);
      refreshCheckInterval = null;
    }

    if (visibilityListener) {
      document.removeEventListener("visibilitychange", visibilityListener);
      visibilityListener = null;
    }

    isTokenRefreshActive.value = false;
  };

  onUnmounted(() => {
    stopTokenRefresh();
  });

  return {
    startTokenRefresh,
    stopTokenRefresh,
    isTokenRefreshActive,
    getTokenDebugInfo,
  };
}
